import create from 'zustand';

import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';

import { BondBlockerType, BondsModalTypeEnum } from '../constants';

import { useBondOrder } from './useBondOrder';
import { useBondPortfolioStore } from './useBondPortfolioStore';
import { useBondTransaction } from './useBondTransaction';
import { useBondPreviewStore } from './useBondPreview';

type SourcePageValue =
  | 'Product Page'
  | 'Portfolio Detail'
  | 'Portfolio List'
  | 'Watchlist Page';
interface State {
  sourcePage?: SourcePageValue;
  isLoading: boolean;
  error?: unknown;

  isSearchActive: boolean;
  isAllowedToSearch: boolean;

  modalType: BondsModalTypeEnum;
  transactionType: BondTransactionActionEnum;
  blockerType: BondBlockerType;
}

interface Actions {
  toggleSearch(state?: boolean): void;

  openConfirmModal(): void;
  openBlockerModal(blockerType: BondBlockerType): void;
  openTncModal(): void;
  openOrderModal(id: string): Promise<void>;
  openOrderHistoryModal(id: string): Promise<void>;
  openCouponReceipt(symbol?: string): Promise<void>;
  // transactions
  openBuyModal(productId?: string, sourcePage?: SourcePageValue): Promise<void>;
  openSellModal(
    productId: string,
    orderId?: string,
    sourcePage?: SourcePageValue,
  ): Promise<void>;
  openOrderPlacedModal(): void;

  closeModal(): void;
  closeTransactionModal(): void;
  clear(): void;
}

type Store = Actions & State;

const initialState: State = {
  isLoading: false,
  error: undefined,

  isSearchActive: false,
  isAllowedToSearch: false,

  modalType: BondsModalTypeEnum.UNSPECIFIED,
  transactionType: BondTransactionActionEnum.UNSPECIFIED,
  blockerType: BondBlockerType.UNSPECIFIED,
};

export const useBondsModalStore = create<Store>((set, get) => ({
  ...initialState,

  toggleSearch(state) {
    if (typeof state === 'boolean') {
      set({ isSearchActive: state });
      return;
    }

    set((prev) => ({ ...prev, isSearchActive: !prev.isSearchActive }));
  },

  openConfirmModal() {
    set({
      modalType: BondsModalTypeEnum.CONFIRMATION,
    });
  },

  openBlockerModal(blockerType: BondBlockerType) {
    set({
      modalType: BondsModalTypeEnum.BLOCKERS,
      blockerType,
    });
  },

  openTncModal() {
    set({ modalType: BondsModalTypeEnum.TNC });
  },

  async openCouponReceipt(orderId: string) {
    const { couponHistories, loadPortfolioDetails } =
      useBondPortfolioStore.getState();

    if (!couponHistories) {
      await loadPortfolioDetails(orderId, true);
    }

    set({ modalType: BondsModalTypeEnum.COUPON_RECEIPT });
  },

  async openOrderModal(id: string) {
    const { loadOrderDetails } = useBondOrder.getState();
    await loadOrderDetails(id);
    set({ modalType: BondsModalTypeEnum.ORDER });
  },

  async openOrderHistoryModal(id: string) {
    const { loadOrderDetails } = useBondOrder.getState();
    await loadOrderDetails(id);
    set({ modalType: BondsModalTypeEnum.HISTORY });
  },

  async openBuyModal(productId, sourcePage) {
    try {
      const { initiateBuyTransaction } = useBondTransaction.getState();
      set({
        modalType: BondsModalTypeEnum.TRANSACTION,
        transactionType: BondTransactionActionEnum.BUY,
        isAllowedToSearch: true,
        sourcePage,
      });
      await initiateBuyTransaction(productId);
    } catch (error) {
      Promise.reject(error);
    }
  },

  async openSellModal(productId, orderId, sourcePage) {
    try {
      // store the source page
      if (sourcePage) {
        set({ sourcePage });
      }

      // if the order id is provided
      if (orderId) {
        const { initiateSellTransaction } = useBondTransaction.getState();
        // open the sell modal
        await initiateSellTransaction(productId, orderId);
        set({
          modalType: BondsModalTypeEnum.TRANSACTION,
          transactionType: BondTransactionActionEnum.SELL,
          isAllowedToSearch: false,
        });
        return;
      }

      // If not, we must check from the portfolio api
      const { loadPortfolioDetails } = useBondPortfolioStore.getState();

      // if portfolio list is empty, we should fetch the list first
      await loadPortfolioDetails(productId);

      const { transactions } = useBondPortfolioStore.getState();

      // if assets is only one, then open the Sell Transaction modal
      if (transactions.length === 1) {
        const [transaction] = transactions;

        // if loaded is stable earn, then open the blocker modal
        if (transaction.is_stable_earn) {
          set({
            modalType: BondsModalTypeEnum.BLOCKERS,
            blockerType: BondBlockerType.STABLE_EARN,
          });
          return;
        }

        get().openSellModal(productId, transaction.order_code);
        return;
      }

      // open the assets / product selection modal
      set({ modalType: BondsModalTypeEnum.ASSETS });
    } catch (error) {
      Promise.reject(error);
    }
  },

  openOrderPlacedModal() {
    set({ modalType: BondsModalTypeEnum.ORDER_PLACED });
  },

  closeModal() {
    const { modalType, toggleSearch, isSearchActive } = get();
    if (isSearchActive) {
      toggleSearch();
    }

    if (modalType === BondsModalTypeEnum.TNC) {
      // back to confirmation
      set({ modalType: BondsModalTypeEnum.CONFIRMATION });
      return;
    }

    // back to the transaction
    const { product } = useBondPreviewStore.getState();

    const isBackToTransaction = modalType === BondsModalTypeEnum.CONFIRMATION;

    if (isBackToTransaction && product) {
      set({
        modalType: BondsModalTypeEnum.TRANSACTION,
      });
      return;
    }

    // Close the modal
    set({
      modalType: BondsModalTypeEnum.UNSPECIFIED,
    });
  },

  closeTransactionModal() {
    const { closeModal, toggleSearch, isSearchActive } = get();
    const { clearData } = useBondTransaction.getState();
    const { clear: clearPreview } = useBondPreviewStore.getState();
    const { clearPortfolioList } = useBondPortfolioStore.getState();

    if (isSearchActive) {
      toggleSearch();
    }

    clearPreview();
    clearPortfolioList();
    clearData();

    closeModal();
  },

  clear() {
    set(initialState);
  },
}));
